// Card layout adapted from pingdotgg/t3code's ComposerPendingUserInputPanel
// (MIT), ec80933ac: a collapsible banner header, then the question and its
// options aligned with the header text. Amoreg adds request details for app
// approvals and an attachments slot for evidence sent with the answer.
import { type ApprovalRequestId } from "@amoreg/contracts";
import { memo, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { type PendingUserInput } from "../../session-logic";
import {
  derivePendingUserInputProgress,
  type PendingUserInputDraftAnswer,
} from "../../pendingUserInput";
import { CheckIcon, MessageCircleQuestionMarkIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "../ui/collapsible";
import { ChatHostContext } from "./chatHost";
import { ComposerBanner } from "./ComposerBanner";

interface PendingUserInputPanelProps {
  pendingUserInputs: PendingUserInput[];
  respondingRequestIds: ApprovalRequestId[];
  answers: Record<string, PendingUserInputDraftAnswer>;
  questionIndex: number;
  onToggleOption: (questionId: string, optionLabel: string) => void;
  onAdvance: () => void;
  /** Rendered under the options, inside the card; absent when the question takes no files. */
  attachments?: ReactNode;
}

export const ComposerPendingUserInputPanel = memo(function ComposerPendingUserInputPanel({
  pendingUserInputs,
  respondingRequestIds,
  answers,
  questionIndex,
  onToggleOption,
  onAdvance,
  attachments,
}: PendingUserInputPanelProps) {
  if (pendingUserInputs.length === 0) return null;
  const activePrompt = pendingUserInputs[0];
  if (!activePrompt) return null;

  return (
    <ComposerPendingUserInputCard
      key={activePrompt.requestId}
      prompt={activePrompt}
      isResponding={respondingRequestIds.includes(activePrompt.requestId)}
      answers={answers}
      questionIndex={questionIndex}
      onToggleOption={onToggleOption}
      onAdvance={onAdvance}
      attachments={attachments}
    />
  );
});

const ComposerPendingUserInputCard = memo(function ComposerPendingUserInputCard({
  prompt,
  isResponding,
  answers,
  questionIndex,
  onToggleOption,
  onAdvance,
  attachments,
}: {
  prompt: PendingUserInput;
  isResponding: boolean;
  answers: Record<string, PendingUserInputDraftAnswer>;
  questionIndex: number;
  onToggleOption: (questionId: string, optionLabel: string) => void;
  onAdvance: () => void;
  attachments: ReactNode;
}) {
  const progress = derivePendingUserInputProgress(prompt.questions, answers, questionIndex);
  const activeQuestion = progress.activeQuestion;
  const autoAdvanceTimerRef = useRef<number | null>(null);
  const onAdvanceRef = useRef(onAdvance);
  const [optimisticSingleSelect, setOptimisticSingleSelect] = useState<{
    questionId: string;
    optionLabel: string;
  } | null>(null);
  // Collapsing leaves only the header, so a tall question stops covering the
  // thread. It is scoped to one question: the card is keyed by request id, and
  // keeping the collapsed question's id reopens the card when the prompt moves
  // to its next question, which sending from the composer does without a click.
  const [collapsedQuestionId, setCollapsedQuestionId] = useState<string | null>(null);
  const isCollapsed = collapsedQuestionId !== null && collapsedQuestionId === activeQuestion?.id;

  useEffect(() => {
    onAdvanceRef.current = onAdvance;
  }, [onAdvance]);

  useEffect(() => {
    if (!activeQuestion || activeQuestion.multiSelect || !optimisticSingleSelect) {
      return;
    }
    if (optimisticSingleSelect.questionId !== activeQuestion.id) {
      setOptimisticSingleSelect(null);
      return;
    }
    if (
      progress.customAnswer.trim().length === 0 &&
      progress.selectedOptionLabels.includes(optimisticSingleSelect.optionLabel)
    ) {
      setOptimisticSingleSelect(null);
    }
  }, [
    activeQuestion,
    optimisticSingleSelect,
    progress.customAnswer,
    progress.selectedOptionLabels,
  ]);

  // Clear auto-advance timer on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimerRef.current !== null) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
    };
  }, []);

  const handleOptionSelection = useCallback(
    (questionId: string, optionLabel: string) => {
      if (activeQuestion?.multiSelect) {
        onToggleOption(questionId, optionLabel);
        return;
      }
      setOptimisticSingleSelect({ questionId, optionLabel });
      onToggleOption(questionId, optionLabel);
      if (autoAdvanceTimerRef.current !== null) {
        window.clearTimeout(autoAdvanceTimerRef.current);
      }
      autoAdvanceTimerRef.current = window.setTimeout(() => {
        autoAdvanceTimerRef.current = null;
        onAdvanceRef.current();
      }, 200);
    },
    [activeQuestion, onToggleOption],
  );

  // Keyboard shortcut: number keys 1-9 select corresponding options when focus is
  // outside editable fields. Multi-select prompts toggle options in place; single-
  // select prompts keep the existing auto-advance behavior. Collapsed prompts opt
  // out, since the numbers they refer to are not on screen.
  const chatHost = useContext(ChatHostContext);
  useEffect(() => {
    if (!activeQuestion || isResponding || isCollapsed) return;
    const handler = (event: globalThis.KeyboardEvent) => {
      if (chatHost && !chatHost.owns(event.target)) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
        return;
      }
      if (
        target instanceof HTMLElement &&
        target.closest('[contenteditable]:not([contenteditable="false"])')
      ) {
        return;
      }
      const digit = Number.parseInt(event.key, 10);
      if (Number.isNaN(digit) || digit < 1 || digit > 9) return;
      const optionIndex = digit - 1;
      if (optionIndex >= activeQuestion.options.length) return;
      const option = activeQuestion.options[optionIndex];
      if (!option) return;
      event.preventDefault();
      handleOptionSelection(activeQuestion.id, option.label);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [activeQuestion, chatHost, handleOptionSelection, isCollapsed, isResponding]);

  if (!activeQuestion) {
    return null;
  }

  const customAnswerActive = progress.customAnswer.trim().length > 0;
  const questionText = activeQuestion.details
    ? (activeQuestion.summary ?? activeQuestion.question)
    : activeQuestion.question;
  // A tool's own card (`lines`, even none): its question is the heading, its
  // lines the body, and everything else waits under Details.
  const cardLines = activeQuestion.lines;
  const details = activeQuestion.details ? (
    <details className="mt-2 text-xs text-muted-foreground" data-approval-details="">
      <summary className="w-fit cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {cardLines ? "Details" : "View request details"}
      </summary>
      <pre
        className={cn(
          "mt-2 max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/30 p-2 text-foreground/80",
          cardLines ? "font-sans text-xs" : "font-mono text-[11px]",
        )}
        data-approval-detail="complete"
      >
        {activeQuestion.details}
      </pre>
    </details>
  ) : null;

  return (
    <div
      data-pending-user-input-card="true"
      className="px-1 py-1 text-xs/4 [--composer-banner-icon-column:--spacing(7)] sm:[--composer-banner-icon-column:--spacing(6)]"
    >
      <Collapsible
        open={!isCollapsed}
        onOpenChange={(open) => {
          setCollapsedQuestionId(open ? null : activeQuestion.id);
        }}
      >
        <CollapsibleTrigger
          render={<ComposerBanner.Row render={<button type="button" />} />}
          title={
            isCollapsed ? "Show the question and its options" : "Hide the question and its options"
          }
          data-pending-user-input-toggle={isCollapsed ? "collapsed" : "expanded"}
        >
          <ComposerBanner.Icon>
            <MessageCircleQuestionMarkIcon />
          </ComposerBanner.Icon>
          <ComposerBanner.Content>
            {cardLines ? (
              <span className="min-w-0 flex-1 truncate font-medium text-foreground">
                {questionText}
              </span>
            ) : (
              <span className="shrink-0 font-medium text-muted-foreground">
                {activeQuestion.header}
              </span>
            )}
            {isCollapsed && !cardLines ? (
              <span className="min-w-0 flex-1 truncate text-secondary-label">{questionText}</span>
            ) : null}
          </ComposerBanner.Content>
          <ComposerBanner.Actions>
            {prompt.questions.length > 1 ? (
              <span className="text-[10px] font-medium text-muted-foreground tabular-nums">
                {questionIndex + 1}/{prompt.questions.length}
              </span>
            ) : null}
            <ComposerBanner.ToggleIcon expanded={!isCollapsed} />
          </ComposerBanner.Actions>
        </CollapsibleTrigger>
        <CollapsiblePanel>
          <ComposerBanner.Scroll>
            <ComposerBanner.Body className="pe-1 pb-1 wrap-anywhere">
              {cardLines ? (
                cardLines.length > 0 ? (
                  <ul className="space-y-1 text-sm text-foreground/85" data-approval-lines="">
                    {cardLines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : null
              ) : (
                <>
                  <p className="text-sm text-foreground/85">{questionText}</p>
                  {details}
                </>
              )}
              {activeQuestion.multiSelect ? (
                <p className="mt-1 text-secondary-label text-xs">Select one or more options.</p>
              ) : null}
              {activeQuestion.options.length > 0 ? (
                <div
                  className={
                    activeQuestion.details || cardLines
                      ? "mt-3 flex flex-wrap gap-2"
                      : "mt-2 -ms-2.5 space-y-0.5"
                  }
                >
                  {activeQuestion.options.map((option, index) => {
                    const isOptimisticallySelected =
                      optimisticSingleSelect?.questionId === activeQuestion.id &&
                      optimisticSingleSelect.optionLabel === option.label;
                    const isSelected =
                      isOptimisticallySelected ||
                      (!customAnswerActive && progress.selectedOptionLabels.includes(option.label));
                    const shortcutKey = index < 9 ? index + 1 : null;
                    return (
                      <button
                        key={`${activeQuestion.id}:${option.label}`}
                        type="button"
                        disabled={isResponding}
                        onClick={() => {
                          handleOptionSelection(activeQuestion.id, option.label);
                        }}
                        className={cn(
                          "group flex items-center gap-2 rounded-md px-2.5 py-2 text-left outline-none transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
                          activeQuestion.details || cardLines
                            ? "border border-border/70"
                            : "w-full",
                          isSelected
                            ? "bg-muted/55 text-foreground"
                            : "bg-transparent text-foreground/85 hover:bg-muted/30",
                          isResponding ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                        )}
                      >
                        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                          <span className="text-sm font-medium">{option.label}</span>
                          {option.description && option.description !== option.label ? (
                            <span className="text-secondary-label text-[11px]">
                              {option.description}
                            </span>
                          ) : null}
                        </div>
                        {isSelected ? (
                          <CheckIcon className="size-3.5 shrink-0 text-primary" />
                        ) : shortcutKey !== null ? (
                          <kbd className="flex size-5 shrink-0 items-center justify-center text-[10px] font-medium text-muted-foreground tabular-nums">
                            {shortcutKey}
                          </kbd>
                        ) : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
              {cardLines ? details : null}
              {attachments ? <div className="mt-2">{attachments}</div> : null}
            </ComposerBanner.Body>
          </ComposerBanner.Scroll>
        </CollapsiblePanel>
      </Collapsible>
    </div>
  );
});
